'use strict';

(function () {
  if (customElements.get('product-features-carousel')) return;

  class ProductFeaturesCarousel extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.swiper');
      this.countEl = this.querySelector('[data-carousel-count]');
      this.bullets = this.querySelectorAll('[data-carousel-bullet]');
      // The markup renders the slide set twice (for a smoother loop) and Swiper
      // adds its own clones, so use the authored count from data-real-slides for
      // the counter/dots. Fall back to the rendered slide count.
      this.realSlides =
        Number.parseInt(this.dataset.realSlides, 10) ||
        this.querySelectorAll('.swiper-slide').length;
      this.swiper = null;
    }

    connectedCallback() {
      if (!this.slider || typeof Swiper === 'undefined') return;
      this.initSwiper();
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    initSwiper() {
      const root = this.closest('.product-features-carousel');
      const prevButton = root?.querySelector('[data-carousel-prev]');
      const nextButton = root?.querySelector('[data-carousel-next]');

      this.swiper = new Swiper(this.slider, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        grabCursor: true,
        watchOverflow: true,
        loop: true,
        loopAdditionalSlides: 2,
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
        breakpoints: {
          768: {
            spaceBetween: 24,
          },
        },
        on: {
          init: () => {
            this.playVideos();
            this.updatePagination();
            this.refreshSwiper();
          },
          slideChange: () => this.updatePagination(),
          slideChangeTransitionEnd: () => this.playVideos(),
        },
      });

      this.bullets.forEach((bullet) => {
        bullet.addEventListener('click', () => {
          const index = Number.parseInt(bullet.dataset.slideIndex, 10);
          if (!Number.isNaN(index)) {
            // slideToLoop maps the real index to the correct (possibly cloned) slide.
            this.swiper.slideToLoop(index);
          }
        });
      });

      this.slider.querySelectorAll('.product-features-carousel__image').forEach((img) => {
        const refresh = () => this.refreshSwiper();
        if (img.complete) {
          refresh();
        } else {
          img.addEventListener('load', refresh, { once: true });
        }
      });

      window.addEventListener('resize', () => this.refreshSwiper());

      this.querySelectorAll('video').forEach((video) => {
        video.addEventListener('loadedmetadata', () => this.refreshSwiper(), {
          once: true,
        });
        video.addEventListener('pause', function () {
          this.play().catch(() => {});
        });
      });
    }

    playVideos() {
      this.querySelectorAll('video').forEach((video) => {
        video.loop = true;
        video.muted = true;
        video.play().catch(() => {});
      });
    }

    refreshSwiper() {
      this.swiper?.update();
    }

    updatePagination() {
      if (!this.swiper || !this.countEl) return;

      // The track contains duplicated slides + Swiper clones, so map realIndex
      // back into the authored range so the 01/0N counter and dots stay accurate.
      const total = this.realSlides;
      const position = this.swiper.realIndex % total;
      const current = position + 1;

      this.countEl.textContent = `${String(current).padStart(2, '0')}/${String(total).padStart(2, '0')}`;

      this.bullets.forEach((bullet, index) => {
        const isActive = index === position;
        bullet.setAttribute('aria-selected', isActive ? 'true' : 'false');
        bullet.classList.toggle('bg-[#232428]', isActive);
        bullet.classList.toggle('bg-[#999999]', !isActive);
      });
    }
  }

  customElements.define('product-features-carousel', ProductFeaturesCarousel);
})();
